'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Tag } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { DiscountBreakdownItem } from '@/providers/EnhancedCart'

type Props = {
    discountBreakdown: DiscountBreakdownItem[]
    bulkVolumeDiscount: number
    className?: string
}

export function AppliedDiscounts({ discountBreakdown, bulkVolumeDiscount, className }: Props) {
    const t = useTranslations('cart')

    if (!discountBreakdown || discountBreakdown.length === 0) {
        return null
    }

    return (
        <div className={`relative rounded-lg border border-border/50 bg-neutral-100 dark:bg-black backdrop-blur-sm p-4 ${className || ''}`}>
            <div className="flex items-center gap-2 mb-3">
                <motion.div
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                >
                    <Tag className="w-4 h-4 text-[var(--neon-orange)]" />
                </motion.div>
                <p className="font-mono text-xs uppercase tracking-widest text-neutral-800 dark:text-neutral-200">
                    {t('discountsApplied')}
                </p>
            </div>
            <div className="space-y-2">
                {discountBreakdown.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between text-xs">
                        <div className="flex-1 min-w-0">
                            <span className="text-foreground font-mono truncate block">
                                {item.productTitle.length > 25
                                    ? item.productTitle.substring(0, 25) + '...'
                                    : item.productTitle}
                            </span>
                            <span className="text-[10px] font-mono text-neutral-800 dark:text-neutral-200 uppercase tracking-wider">
                                {t('percentOff', { percent: item.discountPercentage })}
                                {item.quantity > 1 && ` (×${item.quantity})`}
                                {item.discountMode === 'bulk' && ` - ${t('bulkDiscountLabel')}`}
                                {item.discountMode === 'manual' && ` - ${t('specialOfferLabel')}`}
                            </span>
                        </div>
                        <span className="font-mono font-medium text-green-600 dark:text-green-400 ml-2 whitespace-nowrap">
                            -{(item.discountAmount / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        </span>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between pt-3 mt-3 border-t border-border/50">
                <span className="font-mono text-xs text-neutral-800 dark:text-neutral-200 uppercase tracking-wider">
                    {t('totalSavings')}
                </span>
                <span className="font-mono text-sm font-medium text-green-600 dark:text-green-400">
                    -{(discountBreakdown.reduce((sum, item) => sum + item.discountAmount, 0) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </span>
            </div>
        </div>
    )
}
